import React, { useState } from 'react';
import { ArrowLeft, Check } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Navbar from './Navbar';
import Footer from './Footer';

const ContactSalesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    position: '',
    phone: '',
    inquiryType: 'integration',
    message: '',
    preferredContact: 'email',
  });

  const [submitStatus, setSubmitStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const BASE_URL = process.env.REACT_APP_BASE_URL;

  const inquiryTypes = [
    { value: 'integration', label: t('contactSalesPage.ehrIntegration', 'EMR integration') },
    { value: 'pricing', label: t('contactSalesPage.enterprisePricing', 'Pricing for an institution') },
    { value: 'documentation', label: t('contactSalesPage.technicalDocumentation', 'Technical documentation') },
    { value: 'partnership', label: t('contactSalesPage.partnershipOpportunities', 'Partnership opportunity') },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus({ submitting: true, success: false, error: null });
    try {
      await axios.post(`${BASE_URL}/send-sales-inquiry`, formData, {
        headers: { 'Content-Type': 'application/json' },
      });
      setSubmitStatus({ submitting: false, success: true, error: null });
      setFormData({
        name: '',
        email: '',
        company: '',
        position: '',
        phone: '',
        inquiryType: 'integration',
        message: '',
        preferredContact: 'email',
      });
    } catch (error) {
      setSubmitStatus({
        submitting: false,
        success: false,
        error: error.response?.data?.message || 'Failed to send inquiry. Please try again.',
      });
    }
  };

  return (
    <div className="bf-page">
      <Navbar />

      {/* Spacer so first content doesn't get hidden behind sticky nav */}
      <div style={{ height: 96 }} />

      <section className="bf-section" style={{ paddingTop: 80, paddingBottom: 80 }}>
        <div className="bf-container">
          {/* Back to home */}
          <button
            onClick={() => navigate('/')}
            className="bf-small"
            style={{
              background: 'transparent',
              border: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              color: 'var(--bf-muted)',
              cursor: 'pointer',
              padding: 0,
              marginBottom: 40,
            }}
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{t('contactSalesPage.backToHome', 'Back to home')}</span>
          </button>

          {/* Section head */}
          <div className="bf-section-head" style={{ marginBottom: 56 }}>
            <span className="bf-eyebrow"><span className="dot"></span>{t('contactSalesPage.eyebrow', 'Contact sales')}</span>
            <div>
              <h2>
                {t('contactSalesPage.titleA', 'Let\'s talk about making your EMR ')}
                <span className="bf-grad-text">
                  {t('contactSalesPage.titleB', 'a clinical system.')}
                </span>
              </h2>
            </div>
          </div>

          <div
            className="bf-contact-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: '0.85fr 1.15fr',
              gap: 64,
              alignItems: 'start',
            }}
          >
            {/* Left — intro + contact details */}
            <div className="bf-contact-left" style={{ position: 'sticky', top: 132 }}>
              <p className="bf-lead" style={{ color: 'var(--bf-ink-2)' }}>
                {t(
                  'contactSalesPage.readyToTransform',
                  'Tell us about your hospital, your EMR, and the specialties where manual case review is costing your physicians the most time. A named clinical lead from our team will follow up within 24 hours.'
                )}
              </p>

              <div
                style={{
                  marginTop: 36,
                  paddingTop: 28,
                  borderTop: '1px solid var(--bf-rule-2)',
                }}
              >
                <div className="bf-small">{t('contactSalesPage.whatCanWeHelp', 'What we can help with')}</div>
                <div
                  style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}
                >
                  {inquiryTypes.map((type) => (
                    <div
                      key={type.value}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '20px 1fr',
                        gap: 12,
                        alignItems: 'baseline',
                        fontFamily: 'var(--bf-sans)',
                        fontSize: 15,
                        color: 'var(--bf-ink-2)',
                      }}
                    >
                      <span style={{ color: 'var(--bf-accent)' }}>—</span>
                      <span>{type.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div
                style={{
                  marginTop: 32,
                  paddingTop: 24,
                  borderTop: '1px solid var(--bf-rule)',
                }}
              >
                <div className="bf-small">{t('contactSalesPage.getInTouchDirectly', 'Reach us directly')}</div>
                <div
                  style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 14 }}
                >
                  <div>
                    <div className="bf-caption">{t('contactSalesPage.salesPartnerships', 'Sales & partnerships')}</div>
                    <a
                      href="mailto:support@bloodflow.ai"
                      style={{
                        marginTop: 4,
                        display: 'inline-block',
                        fontFamily: 'var(--bf-serif)',
                        fontSize: 20,
                        letterSpacing: '-0.01em',
                        color: 'var(--bf-ink)',
                        textDecoration: 'none',
                        borderBottom: '1px solid var(--bf-ink)',
                      }}
                    >
                      support@bloodflow.ai
                    </a>
                  </div>
                  <div>
                    <div className="bf-caption">{t('contactSalesPage.headquarters', 'Headquarters')}</div>
                    <div style={{ marginTop: 4, fontFamily: 'var(--bf-serif)', fontSize: 18, color: 'var(--bf-ink-2)' }}>
                      {t('contactSalesPage.lisbon', 'Lisbon, Portugal')}
                    </div>
                  </div>
                  <div>
                    <div className="bf-caption">{t('contactSalesPage.fastResponse', 'Response time')}</div>
                    <div style={{ marginTop: 4, fontFamily: 'var(--bf-serif)', fontSize: 18, color: 'var(--bf-ink-2)' }}>
                      {t('contactSalesPage.responseTime', 'Within 24 hours · enterprise inquiries')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right — form */}
            <div
              className="bf-contact-card"
              style={{
                background: 'var(--bf-surface)',
                border: '1px solid var(--bf-rule-2)',
                borderRadius: 16,
                padding: '40px 44px',
                boxShadow: '0 32px 70px -30px rgba(0,0,0,0.7), inset 0 1px 0 0 rgba(255,255,255,0.04)',
              }}
            >
              {submitStatus.success ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                  <div
                    style={{
                      width: 56,
                      height: 56,
                      borderRadius: '50%',
                      background: 'var(--bf-signal-bg)',
                      border: '1px solid color-mix(in oklab, var(--bf-accent) 35%, transparent)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 20px',
                    }}
                  >
                    <Check className="w-7 h-7" style={{ color: 'var(--bf-accent)' }} />
                  </div>
                  <h3 style={{ marginBottom: 12 }}>{t('contactSalesPage.messageSent', 'Message sent.')}</h3>
                  <p className="bf-body-p" style={{ maxWidth: '36ch', margin: '0 auto', color: 'var(--bf-muted)' }}>
                    {t(
                      'contactSalesPage.thankYou',
                      "Thank you for reaching out. A named clinical lead from our team will get back to you within 24 hours."
                    )}
                  </p>
                  <button
                    onClick={() => setSubmitStatus({ submitting: false, success: false, error: null })}
                    className="bf-small"
                    style={{
                      marginTop: 24,
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--bf-accent)',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    {t('contactSalesPage.sendAnother', 'Send another message')} →
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>
                  <div className="bf-small">{t('contactSalesPage.formLabel', 'Enterprise inquiry')}</div>
                  <h3 style={{ marginTop: 8 }}>{t('contactSalesPage.contactOurTeam', 'Send us the details.')}</h3>
                  <p className="bf-body-p" style={{ marginTop: 8, fontSize: 15, color: 'var(--bf-muted)' }}>
                    {t('contactSalesPage.fillOutForm', "Fill out the form below and we'll follow up directly.")}
                  </p>

                  {submitStatus.error && (
                    <div
                      style={{
                        marginTop: 24,
                        padding: '12px 16px',
                        background: 'var(--bf-signal-bg)',
                        borderLeft: '3px solid var(--bf-accent)',
                        fontFamily: 'var(--bf-sans)',
                        fontSize: 14,
                        color: 'var(--bf-accent-2)',
                      }}
                    >
                      {submitStatus.error}
                    </div>
                  )}

                  <div style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 22 }}>
                    <div className="bf-contact-row">
                      <Field
                        label={t('contactSalesPage.fullName', 'Full name')}
                        required
                      >
                        <input
                          type="text"
                          name="name"
                          value={formData.name}
                          onChange={handleChange}
                          required
                          placeholder="Dr. João Silva"
                          className="bf-input"
                        />
                      </Field>
                      <Field
                        label={t('contactSalesPage.workEmail', 'Work email')}
                        required
                      >
                        <input
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                          required
                          placeholder="joao@hospital.pt"
                          className="bf-input"
                        />
                      </Field>
                    </div>

                    <div className="bf-contact-row">
                      <Field
                        label={t('contactSalesPage.companyInstitution', 'Institution')}
                        required
                      >
                        <input
                          type="text"
                          name="company"
                          value={formData.company}
                          onChange={handleChange}
                          required
                          placeholder="Hospital / health system"
                          className="bf-input"
                        />
                      </Field>
                      <Field label={t('contactSalesPage.positionTitle', 'Role')}>
                        <input
                          type="text"
                          name="position"
                          value={formData.position}
                          onChange={handleChange}
                          placeholder="CTO · CMIO · Clinical lead"
                          className="bf-input"
                        />
                      </Field>
                    </div>

                    <div className="bf-contact-row">
                      <Field label={t('contactSalesPage.phoneNumber', 'Phone')}>
                        <input
                          type="tel"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                          placeholder="+351 ..."
                          className="bf-input"
                        />
                      </Field>
                      <Field
                        label={t('contactSalesPage.inquiryType', 'Inquiry type')}
                        required
                      >
                        <select
                          name="inquiryType"
                          value={formData.inquiryType}
                          onChange={handleChange}
                          required
                          className="bf-input"
                        >
                          {inquiryTypes.map((type) => (
                            <option key={type.value} value={type.value}>
                              {type.label}
                            </option>
                          ))}
                        </select>
                      </Field>
                    </div>

                    <Field label={t('contactSalesPage.preferredContactMethod', 'Preferred contact method')}>
                      <div className="bf-radio-seg" role="radiogroup">
                        {[
                          { v: 'email', l: t('contactSalesPage.email', 'Email') },
                          { v: 'phone', l: t('contactSalesPage.phoneCall', 'Phone call') },
                        ].map((o) => (
                          <label
                            key={o.v}
                            className={'bf-radio-pill' + (formData.preferredContact === o.v ? ' on' : '')}
                          >
                            <input
                              type="radio"
                              name="preferredContact"
                              value={o.v}
                              checked={formData.preferredContact === o.v}
                              onChange={handleChange}
                            />
                            {o.l}
                          </label>
                        ))}
                      </div>
                    </Field>

                    <Field label={t('contactSalesPage.message', 'Message')} required>
                      <textarea
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={6}
                        placeholder={t(
                          'contactSalesPage.messagePlaceholder',
                          'Tell us about your EMR, your specialties, and where manual case review is costing your physicians the most time.'
                        )}
                        className="bf-input"
                        style={{ resize: 'vertical' }}
                      />
                    </Field>
                  </div>

                  <div style={{ marginTop: 32, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 20, flexWrap: 'wrap' }}>
                    <p
                      className="bf-small"
                      style={{
                        color: 'var(--bf-muted-2)',
                        textTransform: 'none',
                        letterSpacing: '0.02em',
                        margin: 0,
                        maxWidth: '36ch',
                      }}
                    >
                      {t(
                        'contactSalesPage.privacyAgreement',
                        'By submitting, you agree to our privacy policy and terms of service.'
                      )}
                    </p>
                    <button
                      type="submit"
                      disabled={submitStatus.submitting}
                      className="bf-btn bf-btn-primary bf-btn-lg"
                      style={{
                        opacity: submitStatus.submitting ? 0.7 : 1,
                        cursor: submitStatus.submitting ? 'not-allowed' : 'pointer',
                      }}
                    >
                      {submitStatus.submitting
                        ? t('contactSalesPage.sending', 'Sending…')
                        : t('contactSalesPage.sendMessage', 'Send message')}{' '}
                      {!submitStatus.submitting && <span className="bf-arrow">→</span>}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>

        <style>{`
          .bf-contact-row {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
          }
          .bf-input {
            width: 100%;
            font-family: var(--bf-sans);
            font-size: 15px;
            color: var(--bf-ink);
            background: transparent;
            border: none;
            border-bottom: 1px solid var(--bf-rule-2);
            padding: 10px 0;
            outline: none;
            transition: border-color 0.15s;
          }
          .bf-input::placeholder { color: var(--bf-muted-2); }
          .bf-input:focus { border-bottom-color: var(--bf-accent); }
          select.bf-input { appearance: none; background-image: linear-gradient(45deg, transparent 50%, var(--bf-muted) 50%), linear-gradient(135deg, var(--bf-muted) 50%, transparent 50%); background-position: calc(100% - 14px) center, calc(100% - 9px) center; background-size: 5px 5px, 5px 5px; background-repeat: no-repeat; padding-right: 30px; cursor: pointer; color-scheme: dark; }
          select.bf-input option { background: var(--bf-surface); color: var(--bf-ink); }
          textarea.bf-input { background: rgba(255,255,255,0.02); }

          .bf-radio-seg { display: inline-flex; gap: 4px; margin-top: 6px; padding: 4px; border: 1px solid var(--bf-rule-2); border-radius: 999px; background: rgba(255,255,255,0.03); }
          .bf-radio-pill { position: relative; display: inline-flex; align-items: center; padding: 7px 16px; border-radius: 999px; font-family: var(--bf-sans); font-size: 13px; font-weight: 500; color: var(--bf-muted); cursor: pointer; transition: color 0.15s, background 0.15s; user-select: none; }
          .bf-radio-pill input { position: absolute; opacity: 0; pointer-events: none; }
          .bf-radio-pill:hover { color: var(--bf-ink); }
          .bf-radio-pill.on { background: var(--bf-surface-2); color: var(--bf-accent); box-shadow: inset 0 0 0 1px color-mix(in oklab, var(--bf-accent) 35%, transparent); }
          @media (max-width: 1000px) {
            .bf-contact-grid { grid-template-columns: 1fr !important; gap: 48px !important; }
            .bf-contact-left { position: static !important; }
          }
          @media (max-width: 600px) {
            .bf-contact-row { grid-template-columns: 1fr !important; }
          }
        `}</style>
      </section>

      <Footer />
    </div>
  );
};

const Field = ({ label, required, children }) => (
  <label style={{ display: 'block' }}>
    <div
      className="bf-caption"
      style={{ marginBottom: 4, color: 'var(--bf-muted)' }}
    >
      {label}
      {required && <span style={{ color: 'var(--bf-accent)', marginLeft: 4 }}>*</span>}
    </div>
    {children}
  </label>
);

export default ContactSalesPage;
